import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { MagnifyingGlassIcon, GearIcon, RocketIcon, ChartBarIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '@/ui/button/Button';
import { useSpace, teamPath } from '@/contexts/space-context';
import { fuzzyMatch } from '@/utils/fuzzy-match';

// ── Types ────────────────────────────────────────────────────────────

interface SpotlightItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string;
}

interface SpotlightSearchProps {
  open: boolean;
  onClose: () => void;
}

// ── Component ────────────────────────────────────────────────────────

export function SpotlightSearch({ open, onClose }: SpotlightSearchProps) {
  const navigate = useNavigate();
  const { activeTeam, teamId } = useSpace();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  function go(path: string) {
    onClose();
    navigate(path);
  }

  // Build static action items
  const actions: SpotlightItem[] = useMemo(() => {
    const items: SpotlightItem[] = [
      {
        id: 'nav-sessions',
        label: 'Go to Sessions',
        description: 'Open the agent sessions list',
        icon: <RocketIcon size={16} />,
        action: () => go(teamPath(teamId, '/sessions')),
        keywords: 'sessions agent runs',
      },
      {
        id: 'nav-settings',
        label: 'Go to Settings',
        description: activeTeam?.is_personal ? 'Personal settings' : (activeTeam?.name ?? 'Settings'),
        icon: <GearIcon size={16} />,
        action: () => go(teamPath(teamId, '/settings')),
        keywords: 'settings preferences team members',
      },
      {
        id: 'nav-usage',
        label: 'Go to Usage',
        description: 'View usage stats',
        icon: <ChartBarIcon size={16} />,
        action: () => go(teamPath(teamId, '/settings/insights/usage')),
        keywords: 'usage billing stats',
      },
    ];

    return items;
  }, [activeTeam, teamId]);

  // Filter results
  const results = useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed) return actions;

    return actions
      .map((item) => {
        const labelScore = fuzzyMatch(trimmed, item.label);
        const kwScore = item.keywords ? fuzzyMatch(trimmed, item.keywords) : null;
        const descScore = item.description ? fuzzyMatch(trimmed, item.description) : null;
        const best = [labelScore, kwScore, descScore]
          .filter((s): s is number => s !== null)
          .reduce((a, b) => Math.max(a, b), -1);
        return best >= 0 ? { item, score: best } : null;
      })
      .filter((x): x is { item: SpotlightItem; score: number } => x !== null)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.item);
  }, [query, actions]);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  // A new query means a new result list, so the selection goes back to the top.
  function changeQuery(next: string) {
    setQuery(next);
    setSelectedIndex(0);
  }

  const handleSelect = useCallback(
    (index: number) => {
      const item = results[index];
      if (item) item.action();
    },
    [results],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          handleSelect(selectedIndex);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [results.length, selectedIndex, handleSelect, onClose],
  );

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-dialog bg-black/30 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-[15%] z-dialog w-full max-w-130 -translate-x-1/2 rounded-xl border border-line-strong bg-surface-2 shadow-(--shadow-overlay) overflow-hidden animate-fade-in"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <Dialog.Title className="sr-only">Search</Dialog.Title>

          {/* Search Input */}
          <div className="flex items-center gap-2 border-b border-line px-3">
            <MagnifyingGlassIcon size={16} className="text-fg-muted shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => changeQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search repositories, actions..."
              className="h-11 flex-1 bg-transparent text-xs text-fg placeholder:text-fg-muted outline-none border-none"
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <Button variant="ghost" size={7} onClick={() => changeQuery('')}>
                <XIcon size={14} />
              </Button>
            )}
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-90 overflow-y-auto py-1">
            {results.length === 0 ? (
              <div className="px-3 py-6 text-center text-2xs text-fg-muted">No results found</div>
            ) : (
              results.map((item, i) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleSelect(i)}
                  onMouseEnter={() => setSelectedIndex(i)}
                  className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-xs transition-colors border-none bg-transparent cursor-pointer ${
                    i === selectedIndex ? 'bg-brand-tint text-fg' : 'text-fg-secondary hover:bg-black/4'
                  }`}
                >
                  <span className={`shrink-0 ${i === selectedIndex ? 'text-brand-text' : 'text-fg-muted'}`}>
                    {item.icon}
                  </span>
                  <span className="truncate font-medium">{item.label}</span>
                  {item.description && (
                    <span className="ml-auto truncate text-2xs text-fg-disabled max-w-[40%] text-right">
                      {item.description}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
